import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Save, Upload, Trash2, ExternalLink, ImageIcon, Inbox, Type, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { UI } from "@/i18n";

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm placeholder:text-white/35 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/25 transition-all";

function LangField({ label, value, onChange, textarea = false }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <div>
      <p className="text-xs font-mono2 uppercase tracking-widest text-white/50 mb-2">{label}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Tag
          rows={textarea ? 3 : undefined}
          value={value?.en || ""}
          onChange={(e) => onChange({ ...value, en: e.target.value })}
          placeholder="English"
          className={inputCls}
        />
        <Tag
          rows={textarea ? 3 : undefined}
          value={value?.es || ""}
          onChange={(e) => onChange({ ...value, es: e.target.value })}
          placeholder="Español"
          className={inputCls}
        />
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="rounded-xl border border-white/10 bg-brand-surface p-6 space-y-5">
      <h3 className="font-display uppercase text-xl text-brand-orange">{title}</h3>
      {children}
    </section>
  );
}

const TABS = [
  { id: "brand", icon: Sparkles },
  { id: "texts", icon: Type },
  { id: "gallery", icon: ImageIcon },
  { id: "leads", icon: Inbox },
];

export default function Admin() {
  const navigate = useNavigate();
  const { t, content, reloadContent } = useSite();
  const [checked, setChecked] = useState(false);
  const [tab, setTab] = useState("brand");
  const [draft, setDraft] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const logoInput = useRef(null);
  const galleryInput = useRef(null);
  const [newCat, setNewCat] = useState("commercial");

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        setChecked(true);
      } catch {
        navigate("/admin/login");
      }
    })();
  }, [navigate]);

  useEffect(() => {
    if (checked && content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [checked, content]);

  useEffect(() => {
    if (!checked) return;
    api
      .get("/quotes")
      .then((r) => setQuotes(r.data))
      .catch(() => {});
  }, [checked]);

  if (!checked) return <div className="min-h-screen bg-brand-navy" />;
  if (!draft) return <div className="min-h-screen bg-brand-navy" />;

  const upd = (fn) =>
    setDraft((d) => {
      const nd = JSON.parse(JSON.stringify(d));
      fn(nd);
      return nd;
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", draft);
      await reloadContent();
      toast.success(t(UI.admin.saved));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/brand/logo", fd);
      await reloadContent();
      toast.success(t(UI.admin.saved));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    e.target.value = "";
  };

  const uploadGallery = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/images", fd);
      const nd = JSON.parse(JSON.stringify(draft));
      nd.gallery = [...(nd.gallery || []), { id: data.id, url: data.url, category: newCat, caption: { en: "", es: "" } }];
      setDraft(nd);
      await api.put("/content", nd);
      await reloadContent();
      toast.success(t(UI.admin.saved));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    e.target.value = "";
  };

  const removeImage = async (id) => {
    const nd = JSON.parse(JSON.stringify(draft));
    nd.gallery = (nd.gallery || []).filter((g) => g.id !== id);
    setDraft(nd);
    try {
      await api.delete(`/images/${id}`).catch(() => {});
      await api.put("/content", nd);
      await reloadContent();
      toast.success(t(UI.admin.saved));
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  const linesList = (v) => (v || "").split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <div className="min-h-screen bg-brand-navy text-[#F1F5F9]">
      <header className="sticky top-0 z-40 bg-brand-navy/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={fileUrl(draft.logo_url)} alt="logo" className="h-9 w-auto rounded border border-white/15" />
            <span className="font-display uppercase tracking-wide truncate">{draft.site_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white px-3 py-2">
              <ExternalLink size={14} /> {t(UI.admin.viewSite)}
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm border border-white/20 rounded-full px-4 py-2 hover:border-brand-red hover:text-brand-red transition-colors">
              <LogOut size={14} /> {t(UI.admin.logout)}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-32">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map(({ id, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                tab === id ? "bg-brand-blue border-brand-blue text-white" : "border-white/15 text-white/65 hover:text-white hover:border-white/40"
              }`}
            >
              <Icon size={15} /> {t(UI.admin.tabs[id])}
            </button>
          ))}
        </div>

        {tab === "brand" && (
          <div className="space-y-6">
            <Card title={t(UI.admin.tabs.brand)}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <img src={fileUrl(draft.logo_url)} alt="logo" className="h-24 w-auto rounded-lg border border-white/15" data-testid="admin-logo-preview" />
                <div>
                  <input ref={logoInput} type="file" accept="image/*" onChange={uploadLogo} className="hidden" id="logo-upload" />
                  <button
                    onClick={() => logoInput.current?.click()}
                    data-testid="admin-logo-upload"
                    className="inline-flex items-center gap-2 bg-brand-orange text-brand-navy font-bold px-5 py-3 rounded-lg hover:scale-[1.02] transition-transform"
                  >
                    <Upload size={16} /> {t(UI.admin.uploadLogo)}
                  </button>
                  <p className="text-xs text-white/50 mt-3">Se muestra en el hero, navbar, footer y al compartir el link (og:image)</p>
                </div>
              </div>
            </Card>

            <Card title="Hero">
              <LangField label="Eyebrow" value={draft.hero.eyebrow} onChange={(v) => upd((d) => (d.hero.eyebrow = v))} />
              {draft.hero.title_lines.map((line, i) => (
                <LangField key={i} label={`Title line ${i + 1}`} value={line} onChange={(v) => upd((d) => (d.hero.title_lines[i] = v))} />
              ))}
              <LangField label="Subtitle" value={draft.hero.subtitle} onChange={(v) => upd((d) => (d.hero.subtitle = v))} textarea />
              <div className="grid sm:grid-cols-2 gap-5">
                <LangField label="CTA primary" value={draft.hero.cta_primary} onChange={(v) => upd((d) => (d.hero.cta_primary = v))} />
                <LangField label="CTA secondary" value={draft.hero.cta_secondary} onChange={(v) => upd((d) => (d.hero.cta_secondary = v))} />
              </div>
            </Card>
          </div>
        )}

        {tab === "texts" && (
          <div className="space-y-6">
            <Card title={t(UI.admin.tabs.texts)}>
              <LangField label="Services title" value={draft.services.title} onChange={(v) => upd((d) => (d.services.title = v))} />
              {draft.services.items.map((s, i) => (
                <div key={i} className="border border-white/10 rounded-lg p-4 space-y-4 bg-brand-navy/40">
                  <LangField label={`Service ${i + 1} title`} value={s.title} onChange={(v) => upd((d) => (d.services.items[i].title = v))} />
                  <LangField label={`Service ${i + 1} description`} value={s.desc} onChange={(v) => upd((d) => (d.services.items[i].desc = v))} textarea />
                  <LangField
                    label={`Service ${i + 1} items (one per line)`}
                    value={{ en: (s.items || []).map((x) => x.en).join("\n"), es: (s.items || []).map((x) => x.es).join("\n") }}
                    onChange={(v) =>
                      upd((d) => {
                        const en = linesList(v.en), es = linesList(v.es);
                        d.services.items[i].items = en.map((x, j) => ({ en: x, es: es[j] || x }));
                      })
                    }
                    textarea
                  />
                </div>
              ))}
            </Card>

            <Card title="Stats">
              {draft.stats.map((s, i) => (
                <div key={i} className="grid sm:grid-cols-[120px_1fr] gap-3 items-center">
                  <input value={s.value} onChange={(e) => upd((d) => (d.stats[i].value = e.target.value))} className={inputCls} data-testid={`admin-stat-value-${i}`} />
                  <LangField label="" value={s.label} onChange={(v) => upd((d) => (d.stats[i].label = v))} />
                </div>
              ))}
            </Card>

            <Card title="Testimonials">
              {draft.testimonials.map((r, i) => (
                <div key={i} className="border border-white/10 rounded-lg p-4 space-y-4 bg-brand-navy/40">
                  <input value={r.name} onChange={(e) => upd((d) => (d.testimonials[i].name = e.target.value))} placeholder="Name" className={inputCls} />
                  <LangField label="Review" value={r.text} onChange={(v) => upd((d) => (d.testimonials[i].text = v))} textarea />
                </div>
              ))}
            </Card>

            <Card title="Marquee">
              <LangField
                label="Items (one per line)"
                value={{ en: (draft.marquee || []).map((m) => m.en).join("\n"), es: (draft.marquee || []).map((m) => m.es).join("\n") }}
                onChange={(v) =>
                  upd((d) => {
                    const en = linesList(v.en), es = linesList(v.es);
                    d.marquee = en.map((x, j) => ({ en: x, es: es[j] || x }));
                  })
                }
                textarea
              />
            </Card>

            <Card title="Quote Calculator">
              {(draft.calculator?.types || []).map((tp, i) => (
                <div key={tp.id || i} className="grid sm:grid-cols-[1fr_100px_100px] gap-3 items-end border-b border-white/5 pb-4">
                  <LangField label={`Piece ${i + 1}`} value={tp.label} onChange={(v) => upd((d) => (d.calculator.types[i].label = v))} />
                  <div>
                    <p className="text-xs font-mono2 uppercase tracking-widest text-white/50 mb-2">Min $</p>
                    <input type="number" value={tp.min} data-testid={`admin-calc-min-${i}`} onChange={(e) => upd((d) => (d.calculator.types[i].min = Number(e.target.value)))} className={inputCls} />
                  </div>
                  <div>
                    <p className="text-xs font-mono2 uppercase tracking-widest text-white/50 mb-2">Max $</p>
                    <input type="number" value={tp.max} data-testid={`admin-calc-max-${i}`} onChange={(e) => upd((d) => (d.calculator.types[i].max = Number(e.target.value)))} className={inputCls} />
                  </div>
                </div>
              ))}
            </Card>

            <Card title="Service areas (one per line)">
              <LangField
                label="Areas"
                value={{ en: (draft.service_areas || []).map((m) => m.en).join("\n"), es: (draft.service_areas || []).map((m) => m.es).join("\n") }}
                onChange={(v) =>
                  upd((d) => {
                    const en = linesList(v.en), es = linesList(v.es);
                    d.service_areas = en.map((x, j) => ({ en: x, es: es[j] || x }));
                  })
                }
                textarea
              />
            </Card>

            <Card title="Before & After images">
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={draft.before_after?.before_url || ""} onChange={(e) => upd((d) => (d.before_after.before_url = e.target.value))} placeholder="Before image URL" className={inputCls} data-testid="admin-ba-before" />
                <input value={draft.before_after?.after_url || ""} onChange={(e) => upd((d) => (d.before_after.after_url = e.target.value))} placeholder="After image URL" className={inputCls} data-testid="admin-ba-after" />
              </div>
            </Card>
          </div>
        )}

        {tab === "gallery" && (
          <div className="space-y-6">
            <Card title={t(UI.admin.uploadImage)}>
              <div className="flex flex-col sm:flex-row items-stretch gap-3">
                <select value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="admin-gallery-category" className={inputCls + " sm:w-56"}>
                  <option value="residential">Residential</option>
                  <option value="commercial">Restaurants & Commercial</option>
                  <option value="custom">Custom Work</option>
                </select>
                <input ref={galleryInput} type="file" accept="image/*" onChange={uploadGallery} className="hidden" id="gallery-upload" />
                <button
                  onClick={() => galleryInput.current?.click()}
                  data-testid="admin-gallery-upload"
                  className="inline-flex items-center justify-center gap-2 bg-brand-blue font-bold px-6 py-2.5 rounded-lg hover:bg-brand-cobalt transition-colors"
                >
                  <Upload size={16} /> {t(UI.admin.uploadImage)}
                </button>
              </div>
            </Card>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(draft.gallery || []).map((img) => (
                <div key={img.id} className="rounded-xl border border-white/10 bg-brand-surface overflow-hidden" data-testid={`admin-gallery-item-${img.id}`}>
                  <div className="relative h-40">
                    <img src={fileUrl(img.url)} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeImage(img.id)}
                      data-testid={`admin-gallery-remove-${img.id}`}
                      className="absolute top-2 right-2 p-2 rounded-full bg-brand-red text-white hover:scale-110 transition-transform"
                      aria-label={t(UI.admin.remove)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="p-3 space-y-2">
                    <input
                      value={img.caption?.en || ""}
                      onChange={(e) => upd((d) => (d.gallery.find((x) => x.id === img.id).caption.en = e.target.value))}
                      placeholder="Caption EN"
                      className={inputCls}
                    />
                    <input
                      value={img.caption?.es || ""}
                      onChange={(e) => upd((d) => (d.gallery.find((x) => x.id === img.id).caption.es = e.target.value))}
                      placeholder="Caption ES"
                      className={inputCls}
                    />
                    <select
                      value={img.category}
                      onChange={(e) => upd((d) => (d.gallery.find((x) => x.id === img.id).category = e.target.value))}
                      className={inputCls}
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Restaurants & Commercial</option>
                      <option value="custom">Custom Work</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-4">
            {quotes.length === 0 && <p className="text-white/50" data-testid="admin-leads-empty">{t(UI.admin.noLeads)}</p>}
            {quotes.map((q) => (
              <div key={q._id} className="rounded-xl border border-white/10 bg-brand-surface p-5 flex items-start justify-between gap-4" data-testid={`admin-lead-${q._id}`}>
                <div>
                  <p className="font-semibold">
                    {q.name} - <span className="font-mono2 text-brand-orange">{q.phone}</span>
                  </p>
                  <p className="text-sm text-white/70 mt-1">{q.message || EMPTY}</p>
                  <p className="text-xs text-white/40 mt-2 font-mono2">{q.created_at?.slice(0, 16).replace("T", " ")}</p>
                </div>
                <button
                  onClick={async () => {
                    await api.delete(`/quotes/${q._id}`);
                    setQuotes(quotes.filter((x) => x._id !== q._id));
                  }}
                  data-testid={`admin-lead-delete-${q._id}`}
                  className="p-2 rounded-full border border-white/15 text-white/60 hover:text-brand-red hover:border-brand-red transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-brand-navy/90 backdrop-blur-md border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-save"
            className="inline-flex items-center gap-2 bg-brand-orange text-brand-navy font-bold px-8 py-3 rounded-lg hover:scale-[1.02] disabled:opacity-60 transition-transform"
          >
            <Save size={16} /> {saving ? "..." : t(UI.admin.save)}
          </button>
        </div>
      </div>
    </div>
  );
}
